import { Link, useForm } from "@inertiajs/react"
import {
  IconBluetooth,
  IconTrash,
  IconDotsVertical,
  IconEye,
  IconClock,
} from "@tabler/icons-react"
import { useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useDeviceStatus } from "@/hooks/use-device-status"
import type { Device } from "@/types/device"

interface DeviceCardProps {
  device: Device
}

export function DeviceCard({ device }: DeviceCardProps) {
  const deleteForm = useForm({})
  const { isConnected, getLastSeenAt, updateDeviceStatus, tick } = useDeviceStatus()

  // Sync initial device data with status context
  useEffect(() => {
    updateDeviceStatus(device.id, device.last_seen_at)
  }, [device.id, device.last_seen_at, updateDeviceStatus])

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete "${device.name}"?`)) {
      deleteForm.delete(`/devices/${device.id}`, {
        preserveScroll: true,
      })
    }
  }

  // Use real-time status from context, falling back to prop
  const lastSeenAt = getLastSeenAt(device.id) ?? device.last_seen_at
  const connected = isConnected(device.id)

  // Use tick to ensure formatLastSeen recalculates every second
  const formatLastSeen = (lastSeenAt: string | null, _tick: number) => {
    if (!lastSeenAt) return "Never"
    const date = new Date(lastSeenAt)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSecs = Math.floor(diffMs / 1000)

    // Show seconds for recent activity (better UX for 10-second threshold)
    if (diffSecs < 60) return diffSecs <= 1 ? "Just now" : `${diffSecs}s ago`

    const diffMins = Math.floor(diffSecs / 60)
    if (diffMins < 60) return `${diffMins}m ago`

    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`

    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays}d ago`
  }

  return (
    <Card className="h-full p-4 flex flex-col">
      {/* Header: icon, title, and menu on one line */}
      <div className="flex items-center gap-3 mb-3">
        <div className="size-10 shrink-0 rounded-lg bg-blue-500/10 flex items-center justify-center">
          <IconBluetooth className="size-5 text-blue-500" />
        </div>
        <h3 className="font-semibold text-base leading-tight flex-1 min-w-0 truncate">
          {device.name}
        </h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-touch" className="shrink-0 -mr-2">
              <IconDotsVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link href={`/devices/${device.id}`}>
                <IconEye className="size-4" />
                View Device
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteForm.processing}
            >
              <IconTrash className="size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Last seen and status on one line */}
      <div className="flex items-center justify-between flex-1">
        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
          <IconClock className="size-3.5" />
          {formatLastSeen(lastSeenAt, tick)}
        </p>
        <div className="flex items-center gap-1.5">
          <span className="relative flex size-2">
            <span
              className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
                connected ? "bg-green-400" : "bg-red-400"
              }`}
            />
            <span
              className={`relative inline-flex size-2 rounded-full ${
                connected ? "bg-green-500" : "bg-red-500"
              }`}
            />
          </span>
          <span className={`text-xs ${connected ? "text-green-600" : "text-red-600"}`}>
            {connected ? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="pt-4 border-t">
        <Button asChild variant="outline" size="sm" className="w-full">
          <Link href={`/devices/${device.id}`} prefetch="hover">
            View Device
          </Link>
        </Button>
      </div>
    </Card>
  )
}
